var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defaultSocketOptions, } from './framework/CommandSocket';
import { Timeouts } from './framework/Timeouts';
export class LG {
    constructor(socket, state, log, framework) {
        this.socket = socket;
        this.state = state;
        this.log = log;
        this.framework = framework;
        // other class properties and constructor
        // Video walls made up of multiple LG displays can be treated as one device.
        //
        // Video walls will typically have an RS232 or network connection to the first display and then
        // daisy-chain RS-232 allowing one connection to the first display to be forwarded to all the
        // other displays. The broadcast address is 00.
        //
        // Since RS-232 is not designed to be daisy-chained, it's important to check status messages
        // from the first display as other displays may have their responses clobbered on RS-232.
        //
        // In a daisy-chain application, we send commands to the broadcast address and query one
        // specific device for status.
        //
        // For example, in a 2 display wall with deviceId set to 01, we send commands to deviceId = 00
        // (the broadcast address) and set queryDeviceId to 01 for status requests.
        //
        // For typical control of one display, the deviceId and queryDeviceId would be the same. The
        // default device ID for LG displays is 01.
        this.daisyChainEnabled = false;
        this.deviceId = '01';
        this.queryDeviceId = '01';
        this.queryDeviceIdTrimmed = '1';
        this.isMacAddressSpecified = false;
        this.timeouts = new Timeouts();
        this.busyTimeout = this.timeouts.create();
        // controls
        this.busy = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.busy.value;
            }),
        };
        this.displayBlank = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kd ${this.queryDeviceId} FF`, new RegExp(`d\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG`, 'i'), tags);
                return match[1]
                    ? match[1] === '01' || match[1] === '\x00\x01'
                    : this.state.displayBlank.value;
            }),
            set: (blank, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kd ${this.deviceId} ${blank ? '01' : '00'}`, new RegExp(`d\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG`, 'i'), tags);
                return match[1]
                    ? match[1] === '01' || match[1] === '\x00\x01'
                    : this.state.displayBlank.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.displayFreeze = {
            set: (freeze, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kx ${this.deviceId} ${freeze ? '00' : '01'}`, new RegExp(`x\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG`, 'i'), tags);
                return match[1]
                    ? match[1] === '00' || match[1] === '\x00\x00'
                    : this.state.displayFreeze.value;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(`kx ${this.deviceId} 01`, // turn it off
                    /x\s+[\x000]?[0-9a-f]+\s+OK([\x00\x010-9a-f]{1,2})|NG/i, ['set.displayFreeze']);
                    return match[1] !== undefined;
                }
                catch (error) {
                    if (error.code !== 'sendTimeout')
                        throw error;
                    return false;
                }
            }),
            powerOffRestrictions: ['hasControl', 'set'],
        };
        this.firmwareVersion = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                return this.state.firmwareVersion.value;
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(`fz ${this.queryDeviceId} FF`, new RegExp(`z\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK(.+)x$|NG`, 'i'), ['get.firmwareVersion']);
                    if (match[1] === undefined)
                        return false;
                    this.state.firmwareVersion.update(match[1]);
                    return true;
                }
                catch (error) {
                    if (error.code !== 'sendTimeout')
                        throw error;
                    return false;
                }
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.power = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`ka ${this.queryDeviceId} FF`, new RegExp(`a\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG|MC`, 'i'), tags);
                if (match[1] === undefined)
                    return this.state.power.value;
                return match[1] === '01' || match[1] === '\x00\x01';
            }),
            set: (power, tags) => __awaiter(this, void 0, void 0, function* () {
                // Clear any pending busy timeouts that would set busy back to false. The power set
                // methods will set busy to true and manage the transition back to false.
                this.busyTimeout.clear();
                if (this.isMacAddressSpecified) {
                    if (this.socket.options.allowNoConnection) {
                        return this.setPowerWakeOnLanNetwork(power, tags);
                    }
                    else {
                        return this.setPowerWakeOnLanSerial(power, tags);
                    }
                }
                return this.setPowerSimple(power, tags);
            }),
            powerOffRestrictions: (config) => {
                return this.socket.state === 'connected' ? [] : ['get'];
            },
        };
        this.serialNumber = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`fy ${this.queryDeviceId} FF`, new RegExp(`y\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK(.+)x|NG|MC`, 'i'), tags);
                return match[1] ? match[1] : 'unknown';
            }),
            hasControl: () => __awaiter(this, void 0, void 0, function* () {
                try {
                    const match = yield this.socket.send(`fy ${this.queryDeviceId} FF`, /y\s+[\x000]?[0-9a-f]+\s+OK(.+)x|NG/i, ['get.serialNumber']);
                    if (match[1] === undefined)
                        return false;
                    return true;
                }
                catch (error) {
                    if (error.code !== 'sendTimeout')
                        throw error;
                    return false;
                }
            }),
            powerOffRestrictions: ['get', 'hasControl'],
        };
        this.videoInput = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`xb ${this.queryDeviceId} FF`, new RegExp(`b\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([0-9a-f]{2})|NG`, 'i'), tags);
                return match[1] ? match[1] : this.state.videoInput.value;
            }),
            set: (code, tags) => __awaiter(this, void 0, void 0, function* () {
                // When daisy-chaining, we usually use the broadcast address (00) for sending commands.
                // However, for video input switching, we only send the command to the primary display.
                const match = yield this.socket.send(`xb ${this.daisyChainEnabled ? this.queryDeviceId : this.deviceId} ${code}`, new RegExp(`b\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([0-9a-f]{2})|NG`, 'i'), tags);
                this.state.busy.update(true);
                this.busyTimeout.set(() => this.state.busy.update(false), 1000);
                return match[1] ? match[1] : this.state.videoInput.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        this.volume = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kf ${this.queryDeviceId} FF`, new RegExp(`f\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([0-9a-f]{2})|NG`, 'i'), tags);
                if (match[1] === undefined)
                    return this.state.volume.value;
                return parseInt(match[1], 16);
            }),
            set: (volume, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`kf ${this.deviceId} ${this.padLeft(volume.toString(16), 2)}`, new RegExp(`f\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([0-9a-f]{2})|NG`, 'i'), tags);
                if (match[1] === undefined)
                    return this.state.volume.value;
                return parseInt(match[1], 16);
            }),
            powerOffRestrictions: ['get', 'set'],
            range: { max: 100, min: 0, round: 1 },
        };
        this.volumeMute = {
            get: (tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`ke ${this.queryDeviceId} FF`, new RegExp(`e\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG`, 'i'), tags);
                return match[1]
                    ? match[1] === '00' || match[1] === '\x00\x00'
                    : this.state.volumeMute.value;
            }),
            set: (mute, tags) => __awaiter(this, void 0, void 0, function* () {
                const match = yield this.socket.send(`ke ${this.deviceId} ${mute ? '00' : '01'}`, new RegExp(`e\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG`, 'i'), tags);
                return match[1]
                    ? match[1] === '00' || match[1] === '\x00\x01'
                    : this.state.volumeMute.value;
            }),
            powerOffRestrictions: ['get', 'set'],
        };
        // polling
        this.polls = [
            {
                intervalMs: 2000,
                onePerInterval: true,
                controls: ['power', 'videoInput', 'volume', 'volumeMute', 'displayBlank'],
            },
            {
                intervalMs: 5000,
                pollWhenDisconnected: true,
                function: () => __awaiter(this, void 0, void 0, function* () {
                    if (!this.state.power.value &&
                        this.socket.options.allowNoConnection &&
                        this.socket.getState() !== 'connected') {
                        if (!this.framework.isReconnecting)
                            yield this.framework.reconnect();
                    }
                }),
            },
        ];
        if (this.socket.options.allowNoConnection) {
            // If the display is powered off externally (e.g., IR remote or third party controller),
            // then we will lose the socket connection. If this happens, we update the power state
            // to false.
            this.socket.onConnectionLost(() => {
                this.state.power.update(false, true);
            });
        }
    }
    // phase callbacks
    onConfigure(config) {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
            this.isMacAddressSpecified = config.macAddress !== undefined;
            if (this.isMacAddressSpecified && config.port === undefined) {
                this.log.error(`mac address configured for wake on lan, but no port specified`);
            }
            this.queryDeviceId = (_a = config === null || config === void 0 ? void 0 : config.lgDeviceId) !== null && _a !== void 0 ? _a : '01';
            this.deviceId = (config === null || config === void 0 ? void 0 : config.lgDaisyChainEnabled) ? '00' : this.queryDeviceId;
            this.daisyChainEnabled = (_b = config === null || config === void 0 ? void 0 : config.lgDaisyChainEnabled) !== null && _b !== void 0 ? _b : false;
            // Support responses that may use \x00 instead of 0 as the first digit of the ID.
            this.queryDeviceIdTrimmed = parseInt(this.queryDeviceId, 16).toString(16);
            if (config.macAddress !== undefined) {
                // The mac address is specified, so we can't query the power if the device is powered
                // off. The power is initialized to false, but we need to query it if we can when we
                // first connect.
                if (this.socket.port !== 9761 || this.socket.state === 'connected') {
                    this.state.power.update(yield this.power.get(['power.get']));
                }
            }
        });
    }
    onDisconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            this.timeouts.clearAll();
        });
    }
    // other class methods
    padLeft(value, n) {
        return Array(n - value.length + 1).join('0') + value;
    }
    setPowerSimple(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(6000);
            this.socket.removeCommands('poll');
            const match = yield this.socket.send(`ka ${this.deviceId} ` + (power ? '01' : '00'), new RegExp(`a\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG|MC`, 'i'), tags);
            this.state.busy.update(true);
            if (match[1]) {
                const newPower = match[1] === '01' || match[1] === '\x00\x01';
                this.state.power.update(newPower);
                yield this.timeouts.delay(newPower ? 4000 : 2000);
            }
            this.state.busy.update(false);
            return this.state.power.value;
        });
    }
    setPowerWakeOnLanNetwork(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(10000);
            this.socket.removeCommands('poll');
            if (power) {
                // Some LG displays support a power management (PM) mode in addition to Wake on LAN.
                // Because PM Mode settings can allow the socket to stay connected even when powered
                // off, we send a power command to try to power on the device even when a MAC address is
                // specified for those cases where Wake on LAN is not required but a MAC address is
                // specified.
                if (this.socket.state === 'connected') {
                    yield this.socket.sendAsync(`ka ${this.deviceId} 01`, tags);
                }
                // For direct Ethernet connections where the socket connection is lost when the display
                // is powered off, we first update the power state so the UI will reflect that the power
                // is on, then we block while sending a Wake on LAN request and attempting to reconnect
                // to the display.
                this.state.busy.update(true);
                // Update power with a short delay so that framework.reconnect gets called first. This
                // tells StateBridge we are disconnected so it doesn't change phase back to configuring
                // to query the controls with power off restrictions when it sees that the power is
                // back on.
                this.timeouts.set(() => this.state.power.update(power), 100);
                yield this.framework.reconnect({
                    attemptDelayMs: 1000,
                    initialDelayMs: 2000,
                    numAttempts: 8,
                    onBeforeReconnect: () => __awaiter(this, void 0, void 0, function* () {
                        return this.framework.sendWakeOnLan();
                    }),
                });
                // When updating the busy state, we must use notifyIfUnchanged = true. This is because
                // the driver reconnects as part of powering on, and reconnecting resets all the states.
                // Without notifyIfUnchanged = true, the busy = false notification won't get sent.
                this.state.busy.update(false, true);
                return this.socket.state === 'connected' ? this.power.get(tags) : false;
            }
            else {
                const match = yield this.socket.send(`ka ${this.deviceId} ` + (power ? '01' : '00'), new RegExp(`a\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG|MC`, 'i'), tags);
                this.state.busy.update(true);
                if (match[1]) {
                    const newPower = match[1] === '01' || match[1] === '\x00\x01';
                    this.state.power.update(newPower);
                    yield this.timeouts.delay(6000);
                }
                this.state.busy.update(false);
                return this.state.power.value;
            }
        });
    }
    setPowerWakeOnLanSerial(power, tags) {
        return __awaiter(this, void 0, void 0, function* () {
            this.framework.suspendPolling(6000);
            this.socket.removeCommands('poll');
            if (power) {
                // Some LG displays support a power management (PM) mode in addition to Wake on LAN.
                // Because PM Mode settings can allow the socket to stay connected even when powered
                // off, we send a power command to try to power on the device even when a MAC address is
                // specified for those cases where Wake on LAN is not required but a MAC address is
                // specified.
                yield this.socket.sendAsync(`ka ${this.deviceId} 01`, tags);
                // For serial connections where the display is unresponsive when powered off, first we
                // update the power state so the UI will reflect that the power is on, then we block
                // while sending a Wake on LAN request, then we continue blocking while sending some
                // power requests to confirm that the power is on, finally we return the new power
                // state.
                this.state.busy.update(true);
                this.state.power.update(true);
                let newPower = false;
                for (let i = 0; i < 4; i++) {
                    this.framework.suspendPolling(6000);
                    yield this.framework.sendWakeOnLan();
                    try {
                        const match = yield this.socket.send(`ka ${this.queryDeviceId} FF`, new RegExp(`a\\s+[\\x000]?${this.queryDeviceIdTrimmed}\\s+OK([\\x00\\x010-9a-f]{2})|NG|MC`, 'i'), ['power.get']);
                        newPower = match[1] === '01' || match[1] === '\x00\x01';
                        break;
                    }
                    catch (_a) { }
                }
                this.state.busy.update(false);
                return newPower;
            }
            else {
                return this.setPowerSimple(power, tags);
            }
        });
    }
}
// static properties
LG.driverType = 'socketDriver';
LG.key = '-L6J1y52skPysi1v8USb';
LG.socketOptions = (config) => {
    const baseSocketOptions = {
        // Responses are terminated with an "x", but the kx response also starts with an "x",
        // so we have to use a regexp extractor instead of a simple terminator.
        commandExtractor: {
            terminators: [''],
            // Serial number responses have more digits after the OK. Have also seen displays
            // that only respond with \x00 instead of 0 in the ID and also return \x00\x00
            // instead of 00 for power queries.
            unterminatedRegExp: /.+\s+[\x000-9a-f]+\s+(?:OK|NG|MC).{0,8}x/i,
        },
        sendTerminator: '\r',
    };
    const hasMacAddress = config.macAddress !== undefined;
    if (config.port === undefined) {
        return [
            Object.assign(Object.assign({}, baseSocketOptions), { port: 9761, allowNoConnection: hasMacAddress }),
            Object.assign(Object.assign({}, baseSocketOptions), { port: 4999, idleTimeoutMs: hasMacAddress ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: false }),
        ];
    }
    // Assume port 9761 is direct Ethernet to the device and anything else is via an Ethernet
    // to serial converter.
    return [
        Object.assign(Object.assign({}, baseSocketOptions), { port: config.port, idleTimeoutMs: hasMacAddress && config.port !== 9761 ? 0 : defaultSocketOptions.idleTimeoutMs, allowNoConnection: hasMacAddress && config.port === 9761 }),
    ];
};
